import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cutCropFrom } from './cutCrop';
import { ImageLightbox } from './ImageLightbox';
import { listingImageSrc } from './panels/NoticeProductImages';
import { directionLabel } from './viewDirections';
import { byName, type MaterialInput, type MaterialsAnswer, type TextureCrop } from './materialsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT EACH MATERIAL WAS MEASURED OFF: the clean areas the materials call drew on the close-ups
// of the parts it put on that material, each read inside the part's outline (see
// measure_materials).
//
// IN THE CALL'S OWN PANEL, under its reasoning: it is the evidence for the numbers in the table.
//
// NOTHING IS STORED BUT THE RECTANGLE. The cut happens here, in a canvas, through the proxy
// that makes the pixels readable — exactly as the crop is cut for a call (see cutCrop).
export function MaterialPatches({ answer, inputs }: {
  answer: MaterialsAnswer;
  // The photographs the crops were looked for in, in the order they were sent.
  inputs: MaterialInput[];
}) {
  const { t } = useLanguage();
  const { materials, crops } = answer;

  // A MATERIAL A ROW, a crop a tile — every part made of it, in every photograph.
  const rows = useMemo(() => byName(materials).map((m) => ({
    name: m.name,
    // WHAT THE PATCHES ADDED UP TO: how many the measurement pooled, and the worst share of a
    // patch's pixels kept once the studio, the other material and the ends of the light were
    // thrown out — a number nobody can weigh is a number that gets believed. It was the table's
    // Crops column; it is about these tiles, so it is over them.
    read: answer.stats.find((one) => one.material === m.name),
    shots: crops.filter((c) => c.material === m.name),
  })), [materials, crops, answer.stats]);
  const shots = useMemo(() => rows.flatMap((row) => row.shots), [rows]);

  // Cut on screen, keyed by the rectangle: the same picture and box is one canvas however many
  // times it is drawn.
  const [cut, setCut] = useState<Record<string, string | null>>({});
  useEffect(() => {
    let alive = true;
    void Promise.all(shots.map(async (crop) => {
      const key = keyOf(crop);
      const src = await cutCropFrom(listingImageSrc(crop.imageId), crop.bbox);
      if (alive) setCut((was) => (was[key] === src ? was : { ...was, [key]: src }));
    }));
    return () => { alive = false; };
  }, [shots]);

  // WHICH PATCH IS OPEN, by its rectangle and not by its picture: two materials in one
  // photograph are two crops of one URL, so the URL names neither. See ImageLightbox's
  // stepIds, which exists for exactly this.
  const [zoom, setZoom] = useState<string | null>(null);
  const open = shots.find((c) => keyOf(c) === zoom);

  if (!shots.length) return null;
  return (
    <div className="flex flex-col gap-4">
      {rows.map((row) => (
        <div key={row.name} className="flex flex-col items-center gap-2">
          <p className="text-center text-xs font-semibold text-slate-200">
            {row.name}
            {row.read && (
              <span className="ml-2 font-normal text-slate-400">
                {t('notice.3d.matPatches').replace('{n}', String(row.read.from))}
                {row.read.worst != null && ` · ${t('notice.3d.texKept')} ${Math.round(row.read.worst * 100)}%`}
              </span>
            )}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {row.shots.map((crop) => (
              <div key={keyOf(crop)} className="flex flex-col items-center gap-1">
                {/* WHICH PART, IN WHICH PHOTOGRAPH. */}
                <span className="max-w-[6rem] truncate text-center text-[10px] font-semibold text-slate-300">
                  {crop.part || '·'}
                </span>
                <span className="text-[9.5px] font-bold uppercase tracking-wide text-slate-400">
                  {viewOf(inputs, crop, false) || '·'}
                </span>
                <Swatch crop={crop} src={cut[keyOf(crop)]} onOpen={() => setZoom(keyOf(crop))} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* OPEN ON THE RECTANGLE, not on the chair it was cut from: what was pressed is a patch
          of wood, and a photograph of the whole chair answers a question nobody asked. The
          photograph itself rather than the cut tile, because the cut is capped at 384 pixels
          for the call it was made for and the zoom is where the pixels get looked at. */}
      <ImageLightbox
        src={open ? listingImageSrc(open.imageId) : null}
        zoomTo={open ? open.bbox : null}
        view={open ? viewOf(inputs, open) : undefined}
        stepIds={shots.map(keyOf)}
        stepId={zoom ?? undefined}
        onNavigate={setZoom}
        onClose={() => setZoom(null)}
      />
    </div>
  );
}

const keyOf = (c: TextureCrop) => `${c.imageId}#${c.material}#${c.bbox.join(',')}`;

// WHAT THE ZOOM IS OF: the photograph's direction and the material, which is the pair that
// places a patch — four pictures times two materials, and "Front" alone names two of them.
const viewOf = (inputs: MaterialInput[], crop: TextureCrop, full = true) => {
  const view = inputs.find((one) => one.id === crop.imageId)?.view;
  return [view ? directionLabel(view) : null, ...(full ? [crop.part, crop.material] : [])]
    .filter(Boolean).join(' · ');
};

// ONE SWATCH, and where in its photograph it came from. The box as percentages — the precision
// that tells two rectangles apart and no further.
//
// `undefined` is still being cut; `null` is a cut that failed — a photograph that would not
// load, or a rectangle too small to hold two pixels. Different states, and the tile says which.
function Swatch({ crop, src, onOpen }: {
  crop: TextureCrop;
  src?: string | null;
  onOpen: () => void;
}) {
  const pc = (v: number) => (v * 100).toFixed(1);
  return (
    <figure className="flex w-20 flex-col items-center gap-0.5">
      <button
        type="button"
        onClick={onOpen}
        disabled={!src}
        className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-slate-600 bg-white transition-opacity hover:opacity-90 disabled:cursor-default"
      >
        {src === undefined && <Loader2 className="h-4 w-4 animate-spin text-slate-300" />}
        {src === null && <span className="text-[10px] text-slate-400">—</span>}
        {src && <img src={src} alt="" className="h-full w-full cursor-zoom-in object-cover" />}
      </button>
      <figcaption className="text-center font-mono text-[9.5px] leading-tight tabular-nums text-slate-400">
        x {pc(crop.bbox[0])}–{pc(crop.bbox[2])}
        <br />
        y {pc(crop.bbox[1])}–{pc(crop.bbox[3])}
      </figcaption>
    </figure>
  );
}
